import React from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "component/misc/CircularProgress";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import Tooltip from "./Tooltip";
import { proxiedLogoUrl, hideBrokenImage } from "utils/logoUrl";
import { translateSectorLabel } from "utils/sectorLabel";

const NewsDetailPanel = ({ progress, sectors, reading_time_minutes, create_date, linkCount, source_logos = [], sources = [] }) => {
    const { t, i18n } = useTranslation();
    const maxVisibleSources = 9;
    const berlinTime = create_date ? toZonedTime(new Date(create_date), "Europe/Berlin") : null;
    const formattedDate = berlinTime ? format(berlinTime, "dd.MM.yyyy - HH:mm") : "";

    const sourceEntries = Array.isArray(sources) && sources.length > 0
        ? sources
        : Array.isArray(source_logos)
            ? source_logos.map((logo) => ({ logo_url: logo, name: t("news.sources"), article_url: null }))
            : [];

    const displayedSources = sourceEntries.slice(0, maxVisibleSources);
    const hiddenSources = sourceEntries.slice(displayedSources.length);
    // Only show "+N" for sources we can actually render in the dropdown.
    const remainingSources = hiddenSources.length;

    return (
        <div className='custom_panel_box news-detail-panel'>
            {/* Top Panel */}
            <div className="custom_top_section mb-4 bg_glassEffect">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <span className="small fw-semibold">{formattedDate}</span>
                    <div className="d-flex align-items-center gap-2">
                        <span className="custom-readtime">{reading_time_minutes} {t("news.readTime.min")}</span>
                        <span><CircularProgress progress={progress} /></span>
                    </div>
                </div>

                <div className="news_detail d-flex justify-content-between align-items-center mb-4">
                    <span className="small fw-semibold">{t("news.tags")}</span>
                    <div className="d-flex align-items-center gap-2">
                        {sectors && sectors.slice(0, 2).map((sector, idx) => (
                            <span key={idx} className='category rounded-1 text-white'>{translateSectorLabel(sector, i18n.resolvedLanguage || i18n.language)}</span>
                        ))}
                    </div>
                </div>

                <div className="news_detail d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-semibold">{t("news.sources")}</span>
                    <div className="news_sourace_block d-flex align-items-center gap-2">
                        <div className="source-avatars d-flex">
                            {displayedSources.map((source, index) => {
                                const href = source?.article_url || "";
                                const tooltipText = source?.name || t("news.sources");
                                const logo = source?.logo_url || source?.logo || "";
                                return (
                                    <a
                                        key={logo || href || index}
                                        href={href || "#"}
                                        target={href ? "_blank" : undefined}
                                        rel={href ? "noopener noreferrer" : undefined}
                                        className='source_avtar_link'
                                        aria-label={`${t("news.source.label")}: ${tooltipText}`}
                                        tabIndex={0}
                                    >
                                        <Tooltip text={tooltipText}>
                                            <img src={proxiedLogoUrl(logo)} alt={tooltipText} className="source-icon" loading="lazy" onError={hideBrokenImage} />
                                        </Tooltip>
                                    </a>
                                );
                            })}
                        </div>
                        {remainingSources > 0 && (
                            <div className="source-more-wrapper">
                                <span className="text-light small source-more-trigger">+{remainingSources}</span>
                                <div className="source-more-popover">
                                    <div className="source-more-title">{t("news.sources")}</div>
                                    <div className="source-more-list">
                                        {hiddenSources.map((source, index) => {
                                            const href = source?.article_url || "";
                                            const name = source?.name || t("news.sources");
                                            const logo = source?.logo_url || source?.logo || "";
                                            const key = `${name}-${href}-${index}`;
                                            if (!href) {
                                                return (
                                                    <div className="source-more-item" key={key}>
                                                        {logo && <img src={proxiedLogoUrl(logo)} alt={name} className="source-more-icon" loading="lazy" onError={hideBrokenImage} />}
                                                        <span className="source-more-name">{name}</span>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <a
                                                    className="source-more-item source-more-link"
                                                    key={key}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {logo && <img src={proxiedLogoUrl(logo)} alt={name} className="source-more-icon" loading="lazy" onError={hideBrokenImage} />}
                                                    <span className="source-more-name">{name}</span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsDetailPanel;
